import { maxBy } from "lodash"
import z from "zod"

import { createId, jsonDbAtom } from "@axtesys/react-tools"

import { operatorSelector } from "~shared/feature/Authentication/state"
import {
  padCashRegisterPart,
  padReceiptPart,
} from "~shared/feature/Receipt/lib"
import { selector } from "~shared/lib/recoil/lib"
import {
  DisplayReceipt,
  DisplayReceiptEntity,
  ReceiptId,
} from "~shared/types/Receipt"

import {
  cashRegisterState,
  cashRegisterWithoutNumberSelector,
} from "../CashRegister/state"
import { companyState } from "../OfflineData/state"
import {
  outputPaymentsSelector,
  payoutsSelector,
  receiptTextState,
  tipsSelector,
} from "../Payment/state"
import {
  cartDiscountHistoryState,
  cartItemsHistoryState,
  cartTotalRoundingDifferenceSelector,
  currentInvoiceContactBillingAddressDataSelector,
  currentInvoiceDepartmentDataSelector,
  currentInvoiceShippingAddressDataSelector,
  discountableCartItemsPresentSelector,
  evaluatedCartItemsSelector,
} from "../ShoppingCart/state"
import { autoPrintPrinterIdState } from "../Walkthrough/state"
import { createReceipt } from "./lib"

// ID created on client changes with every cart.
//
// Attention: Do not change the key of this atom,
// as it would reset on the next initialisation.
// This would lead to several errors and problems.
export const receiptIdState = jsonDbAtom({
  key: "invoiceId",
  default: createId(),
  schema: z.string(),
})

// Receipt count received from the server during pairing.
// Is incremented every time we successfully submit a receipt.
//
// Attention: Do not change the key of this atom,
// as it would reset on the next initialisation.
// This would lead to several errors and problems.
export const receiptCountState = jsonDbAtom({
  key: "invoiceCount",
  default: 1,
  schema: z.number(),
})

// The effective `receiptNumber` we print on the receipt.
// Something like 'K02-000124'.
export const receiptNumberSelector = selector({
  key: "receiptNumber",
  get: ({ get }) => {
    const receiptNumber = get(receiptCountState)
    const { cashRegisterNumber } = get(cashRegisterState)

    const registerPart = padCashRegisterPart(cashRegisterNumber.toString())
    const receiptPart = padReceiptPart(receiptNumber.toString())

    return `K${registerPart}-${receiptPart}`
  },
})

export const receiptSelector = selector({
  key: "receiptSelector",
  get: ({ get }) => {
    let cartDiscountsHistory = get(cartDiscountHistoryState)

    if (
      cartDiscountsHistory.length > 0 &&
      !get(discountableCartItemsPresentSelector) &&
      maxBy(
        cartDiscountsHistory,
        historyEntry => historyEntry.createdAt,
      )!.discounts.some(discount => discount.absolute || discount.percent)
    ) {
      cartDiscountsHistory = [
        ...cartDiscountsHistory,
        { discounts: [], createdAt: new Date() },
      ]
    }

    return createReceipt({
      receiptId: get(receiptIdState),

      company: get(companyState),
      cashRegister: get(cashRegisterWithoutNumberSelector),
      operator: get(operatorSelector),

      printerId: get(autoPrintPrinterIdState) ?? "email",
      receiptNumber: get(receiptNumberSelector),

      itemsHistory: get(cartItemsHistoryState),
      items: get(evaluatedCartItemsSelector),
      cartDiscountsHistory,

      roundingDifference: get(cartTotalRoundingDifferenceSelector),
      payments: get(outputPaymentsSelector),
      tips: get(tipsSelector),
      payouts: get(payoutsSelector),

      billingAddress: get(currentInvoiceContactBillingAddressDataSelector),
      department: get(currentInvoiceDepartmentDataSelector),
      shippingAddress: get(currentInvoiceShippingAddressDataSelector),
      receiptText: get(receiptTextState),
    })
  },
})

export const lastSuccessfulReceiptIdState = jsonDbAtom({
  key: "lastSuccessfulReceiptId",
  default: undefined as ReceiptId | undefined,
  schema: z.ostring(),
})

export const lastSuccessfulCancelReceiptIdState = jsonDbAtom({
  key: "lastSuccessfulCancelReceiptId",
  default: undefined as ReceiptId | undefined,
  schema: z.ostring(),
})

export const latestCompletedDisplayReceiptState = jsonDbAtom({
  key: "latestCompletedDisplayReceipt",
  default: undefined as DisplayReceipt | undefined,
  schema: DisplayReceiptEntity.schema,
  serialize: DisplayReceiptEntity.serialize,
  deserialize: DisplayReceiptEntity.deserialize,
})
